import { SxProps, Theme } from '@mui/material';
import { colors, typography, vault, vaultEffects, vaultLayout, vaultMedia, vaultRadii } from './design-tokens';

const focusRing = {
  '&:focus-visible': { outline: `2px solid ${vault.accent}`, outlineOffset: 2 },
};

export const headerStyles: Record<string, SxProps<Theme>> = {
  appBar: {
    backgroundColor: vault.bg,
    backgroundImage: 'none',
    color: vault.fg,
    boxShadow: 'none',
    borderRadius: 0,
    borderBottom: `1px solid ${vault.line}`,
  },
  toolbar: {
    display: 'grid',
    // Nav collapses into the menu below md, leaving logo | actions.
    gridTemplateColumns: { xs: '1fr auto', md: '1fr auto 1fr' },
    alignItems: 'center',
    gap: '16px',
    minHeight: '66px !important',
    padding: `0 ${vaultLayout.gutter} !important`,
    [vaultMedia.mobile]: {
      minHeight: '60px !important',
      padding: `0 ${vaultLayout.gutterMobile} !important`,
    },
  },
  logoLink: {
    justifySelf: 'start',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    ...focusRing,
  },
  // The PNG wordmark is used as a mask so it can be tinted per theme.
  logo: {
    display: 'block',
    height: 16,
    aspectRatio: '1000 / 70',
    backgroundColor: vault.accentText,
    WebkitMaskImage: `url(${process.env.PUBLIC_URL}/logo.png)`,
    maskImage: `url(${process.env.PUBLIC_URL}/logo.png)`,
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    [vaultMedia.mobile]: { height: 12 },
  },
  nav: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    borderRadius: vaultRadii.pill,
    backgroundColor: vault.chip,
    border: `1px solid ${vault.line}`,
  },
  navItem: {
    padding: '7px 16px',
    borderRadius: vaultRadii.pill,
    fontSize: 13,
    lineHeight: 1.3,
    color: vault.muted,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: `background-color ${vaultEffects.transition}, color ${vaultEffects.transition}`,
    '&:hover': { color: vault.fg },
    '&[aria-current="page"]': { backgroundColor: vault.chipStrong, color: vault.fg },
    ...focusRing,
  },
  actions: {
    justifySelf: 'end',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    [vaultMedia.mobile]: { gap: '8px' },
  },
  iconButton: {
    width: 40,
    height: 40,
    color: vault.muted,
    borderRadius: '10px',
    '&:hover': { color: vault.fg, backgroundColor: vault.chip },
    ...focusRing,
  },
  menuButton: {
    width: 40,
    height: 40,
    color: vault.fg,
    borderRadius: '10px',
    border: `1px solid ${vault.lineStrong}`,
    '&:hover': { backgroundColor: vault.chip },
    ...focusRing,
  },
  // Solid fg button — "Sign in" and the logged-in account button.
  solidButton: {
    minWidth: 0,
    padding: '8px 16px',
    borderRadius: vaultRadii.input,
    backgroundColor: vault.fg,
    color: vault.bg,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
    textTransform: 'none',
    boxShadow: 'none',
    gap: '8px',
    '&:hover': { backgroundColor: vault.fg, opacity: 0.88, boxShadow: 'none' },
    ...focusRing,
  },
  textButton: {
    minWidth: 0,
    padding: '8px 4px',
    color: vault.muted,
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': { color: vault.fg, backgroundColor: 'transparent' },
    ...focusRing,
  },
  menu: {
    '& .MuiPaper-root': {
      marginTop: '8px',
      minWidth: 200,
      backgroundColor: vault.surface,
      border: `1px solid ${vault.line}`,
      borderRadius: vaultRadii.card,
      boxShadow: `0 20px 60px ${vault.shadow}`,
      '& .MuiList-root': { padding: '6px' },
    },
  },
  menuItem: {
    minHeight: 40,
    padding: '8px 12px',
    borderRadius: vaultRadii.input,
    fontSize: 14,
    color: vault.fg,
    '&:hover': { backgroundColor: vault.chip },
    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: vault.chipStrong, fontWeight: 600 },
  },
  drawerPaper: {
    width: 300,
    maxWidth: '85vw',
    backgroundColor: vault.surface,
    backgroundImage: 'none',
    borderLeft: `1px solid ${vault.line}`,
    boxShadow: `0 0 60px ${vault.shadow}`,
  },
  drawerHeader: {
    padding: '24px 20px 20px',
    borderBottom: `1px solid ${vault.line}`,
    background: vaultEffects.heroGlowLeft,
  },
  drawerHeaderLabel: {
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: vault.accentText,
    marginBottom: '6px',
  },
  drawerHeaderEmail: {
    fontSize: 14,
    fontWeight: 500,
    color: vault.fg,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  drawerSubheader: {
    padding: '10px 12px 6px',
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: vault.faint,
  },
  drawerListItem: {
    minHeight: 40,
    borderRadius: vaultRadii.input,
    marginBottom: '2px',
    color: vault.muted,
    '& .MuiListItemIcon-root': { minWidth: 36, color: vault.faint },
    '&:hover': {
      backgroundColor: vault.chip,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: vault.fg },
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: vault.chipStrong,
      '& .MuiListItemText-primary': { color: vault.fg, fontWeight: 600 },
    },
  },
  drawerListItemLogout: {
    minHeight: 40,
    borderRadius: vaultRadii.input,
    '& .MuiListItemIcon-root': { minWidth: 36, color: vault.faint },
    '&:hover': {
      backgroundColor: vault.chip,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: colors.accent.red },
    },
  },
  drawerItemText: {
    fontSize: 14,
    fontWeight: 500,
    color: vault.fg,
  },
  drawerDividerSpaced: {
    borderColor: vault.line,
    my: 1,
  },
  drawerDivider: {
    borderColor: vault.line,
  },
};
